import { Alert, Flex, Group, Table, Text, LoadingOverlay, Space } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useState, useMemo } from "react";
import { RankingListPaginator } from "@/modules/diagnostico/components/ranking-list/components/RankingListPaginator";
import { usePaginator } from "@/modules/diagnostico/components/ranking-list/hooks/usePaginator";
import type { EmpresaMedia } from "@/routes/relatorio-page/RelatorioLayout";
import ExportExcel from "./ExportExcel";
import ModalFilter from "./ModalFilter";
import TableMediaItem from "../table-media-item";

interface Filtro {
  search: string;
  filtro: string;
  habilitada: boolean;
}

interface TableMediasProperties {
  empresasComMedia: EmpresaMedia[];
  loading: boolean;
  error: string | null;
}

const mockFilter: Filtro = { search: "", filtro: "", habilitada: false };

const TableMedias = ({ empresasComMedia, loading, error }: TableMediasProperties) => {
  const [filter, setFilter] = useState<Filtro>(mockFilter);

  // Filtra e ordena por nota oficial
  const dados = useMemo(
    () => empresasComMedia.filter((item) => doFilter(item, filter)).sort((a, b) => b.oficial - a.oficial),
    [empresasComMedia, filter]
  );

  const { currentPageContent, currentPage, pageSize, setCurrentPage, setPageSize } = usePaginator(dados);

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size="1rem" />} title="Erro ao carregar dados" color="red">
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Group justify={"space-between"} mb={10}>
        <Text size={"md"} fw={700}>
          Nota média das Empresas
        </Text>
        <Group>
          <ModalFilter setFilter={setFilter} />
          <ExportExcel empresas={dados} />
        </Group>
      </Group>

      <div style={{ overflowX: "auto" }}>
        <LoadingOverlay visible={loading} />

        <Table horizontalSpacing="sm" verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Empresa</Table.Th>
              <Table.Th>CNPJ</Table.Th>
              <Table.Th>Responsável</Table.Th>
              <Table.Th>Telefone</Table.Th>
              <Table.Th>Porte</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th>Categoria</Table.Th>
              <Table.Th>Auto Avaliação</Table.Th>
              <Table.Th>Nota Oficial</Table.Th>
              <Table.Th>Fase</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {currentPageContent.map((item) => (
              <TableMediaItem key={item.id} item={item} />
            ))}
          </Table.Tbody>
        </Table>

        <Flex justify="flex-end" mt="sm">
          <RankingListPaginator
            numberOfItems={dados.length}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </Flex>
      </div>

      <Space h="md" />
    </>
  );
};

export default TableMedias;

// Funções auxiliares de filtro
/* eslint-disable sonarjs/cognitive-complexity */

const doFilter = (item: EmpresaMedia, filter: Filtro) => {
  if (!item) return true;
  if (!filter.search && !filter.filtro && !filter.habilitada) return true;
  try {
    const filterMap: Record<string, (item: EmpresaMedia) => string> = {
      Empresa: (index) => index.nomeFantasia || "",
      Cnpj: (index) => index.cnpj || "",
      Responsavel: (index) => index.responsavel?.nome || "",
      Telefone: (index) => index.telefone || "",
      Porte: (index) => index.porteOriginal || "",
      Estado: (index) => index.endereco?.estado || "",
      Media: (index) => (index.media === undefined ? "" : index.media.toString()),
      Categoria: (index) => index.categorias ?? ""
    };
    const getValue = filterMap[filter.filtro] || (() => "");
    const itemValue = getValue(item);

    const matchesSearch = filter.search ? (itemValue?.toLowerCase() ?? "").includes(filter.search.toLowerCase()) : true;
    const matchesHabilitada = filter.habilitada ? item.habilitada === filter.habilitada : true;
    return matchesSearch && matchesHabilitada;
  } catch (error) {
    console.error("Erro ao filtrar dados", error);
    return false;
  }
};
